<template>
    <div class="centered-block">
        <div class="filter__block">
            <div class="filter__block_items">
                <div class="map__filter_item outlined-button open-filter" @click="openFilterPopup">
                    <svg-icon name="filter" class="icon"/>
                    <span class="text">{{ $s("STR_FILTER") }}</span>
                </div>
                <div
                    v-if="selectedRegion !== 'all'"
                    class="map__filter_item outlined-button filter-item"
                >
                    <span class="text">
                        {{ getRegionTitleById(selectedRegion) }}
                    </span>
                    <div class="close__btn" @click="clearRegionFilter">
                        <svg-icon name="close_2" class="icon" />
                    </div>
                </div>
                <div
                    v-for="paramId in selectedFilterParams"
                    :key="'param-' + paramId"
                    class="map__filter_item outlined-button filter-item"
                >
                    <span class="text">{{ getParamTitleById(paramId) }}</span>
                    <div class="close__btn" @click="removeParamFilter(paramId)">
                        <svg-icon name="close_2" class="icon" />
                    </div>
                </div>
                <div class="map__filter_item reset-filter" @click="resetFilters">
                    <svg-icon name="filter-reset" class="icon"/>
                    <span class="text">{{ $s("STR_FILTER_RESET") }}</span>
                </div>
            </div>
        </div>
    </div>
    <div class="map__block">
        <div class="map" ref="map"></div>
    </div>

    <div v-if="showFilterPopup" class="filter__popup-overlay">
        <div class="filter__popup">
            <div class="filter__popup-header">
                <span class="filter__popup_title">{{ $s("STR_POPUP_FILTER_TITLE") }}</span>
                <div class="close-btn" @click="closeFilterPopup">
                    <svg-icon name="close_2" class="icon"/>
                </div>
            </div>

            <div class="filter__popup-content">
                <div 
                    class="filter__item floating-label-input" 
                    :class="{ 'not-selected': !tempSelectedRegion || tempSelectedRegion === 'all' }"
                >
                    <span class="input-label">{{ $s("STR_ALL_REGION") }}</span>
                    <select class="select" v-model="tempSelectedRegion">
                        <option value="all">{{ $s("STR_ALL_REGIONS") }}</option>
                        <option v-for="region in regions" :key="region.id" :value="region.id">
                            {{ region.title }}
                        </option>
                    </select>
                </div>

                <div class="filter__params">
                    <div class="filter__params_item" v-for="filterParam in filterParams" :key="filterParam.id">
                        <label class="label__item">
                            <input
                                type="checkbox"
                                :value="filterParam.id"
                                v-model="tempSelectedFilterParams"
                            />
                            <span class="text">{{filterParam.title}}</span>
                        </label>
                    </div>
                </div>
            </div>
            <div class="filter__popup_btns">
                <button class="button" @click="applyFilters">
                    {{ $s("STR_FILTER_APPLY") }}
                </button>
                <button class="filter-reset" @click="resetFilters">
                    <svg-icon name="filter-reset" class="icon"/>
                    <span class="text">{{ $s("STR_FILTER_RESET") }}</span>
                </button>
            </div>
        </div>
    </div>

    <div v-if="activeMarker" class="marker__popup_overlay">
        <div class="marker__popup">
            <div class="marker__popup_header">
                <span class="marker__popup_title">{{ activeMarker.title }}</span>
                <div class="close__btn" @click="closeMarkerPopup">
                    <svg-icon name="close_2" class="icon"/>
                </div>
            </div>
            <div class="marker__popup_content">
                <div class="slider__block" v-if="Array.isArray(activeMarker.slider_images) && activeMarker.slider_images.length > 0">
                    <button class="swiper-button-prev">
                        <svg-icon name="slider-prev" class="icon" />
                    </button>
                    <button class="swiper-button-next">
                        <svg-icon name="slider-next" class="icon" />
                    </button>
                    <swiper
                        :modules="modules"
                        :loop="true"
                        :navigation="{
                            prevEl: '.swiper-button-prev',
                            nextEl: '.swiper-button-next',
                        }"
                        class="marker-swiper"
                    >
                        <swiper-slide v-for="(slide_item, index) in activeMarker.slider_images" :key="index">
                            <div class="image-container" v-html="slide_item.image"></div>
                            <a class="image__link" :href="slide_item.link" :data-fancybox="'gallery-' + activeMarker.id">
                                <div class="btn">
                                    <svg-icon name="zoom" class="icon" />
                                </div>
                            </a>
                        </swiper-slide>
                    </swiper>
                </div>
                <div class="marker__popup_content-item" v-if="getRegionTitleById(activeMarker.region_id)">
                    <div class="marker__popup_content-title">
                        {{ $s("STR_MARKER_REGION") }}
                    </div>
                    <div class="marker__popup_content-text">
                        {{ getRegionTitleById(activeMarker.region_id) }}
                    </div>
                </div>

                <div class="marker__popup_content-item" v-if="activeMarker.title">
                    <div class="marker__popup_content-title">
                        {{ $s("STR_MARKER_NAME") }}
                    </div>
                    <div class="marker__popup_content-text">
                        {{ activeMarker.title }}
                    </div>
                </div>

                <div class="marker__popup_content-item" v-if="activeMarker.description">
                    <div class="marker__popup_content-text" v-html="activeMarker.description"></div>
                </div>

                <div class="marker__popup_content-params marker__popup_content-item" v-if="Array.isArray(activeMarker.filter_params_ids) && activeMarker.filter_params_ids.length > 0">
                    <div class="marker__popup_content-title">
                        {{ $s("STR_MARKER_PARAMS") }}
                    </div>
                    <div class="marker__popup_content-params_items">
                        <div
                            class="marker__popup_content-param"
                            v-for="filter_param_id in activeMarker.filter_params_ids" 
                            :key="filter_param_id"
                        >
                            {{ getParamTitleById(filter_param_id) }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import DG from '2gis-maps';
import ajaxRequest from "@/utils/ajaxRequest.js";
import noScroll from "@/utils/noScroll.js";

import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';

import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";

import { nextTick } from 'vue';

export default {
	components: {
		Swiper,
		SwiperSlide
	},
    props: {
        regions: {
            type: Array,
            required: true,
        },
        filterParams: {
            type: Array,
            required: true,
        }
    },
    data() {
        return {
            map: null,
            markers: [],
            markerLayer: null,
            showFilterPopup: false,
            selectedRegion: 'all',
            tempSelectedRegion: 'all',
            selectedFilterParams: [],
            tempSelectedFilterParams: [],
            activeMarker: null,
        };
    },
    setup() {
        const onSwiper = (swiper) => {
            // console.log(swiper);
        };
        return {
            onSwiper,
            modules: [Navigation],
        };
    },
    async mounted() {
        this.initMap();
        await this.loadMarkers();
    },
    watch: {
        selectedRegion() {
            this.renderMarkers();
        },
        activeMarker(val) {
            if (val) {
                nextTick(() => {
                    Fancybox.bind(`[data-fancybox="gallery-${val.id}"]`, {
                        // groupAll: true,
                    });
                });
            }
        },
    },
    methods: {
        initMap() {
            this.map = DG.map(this.$refs.map, {
                'center': [43.231392, 76.94543],
                'zoom': 18,
                'scrollWheelZoom': false,
            });

            // this.markerLayer = DG.featureGroup().addTo(this.map);
            
            // this.markerLayer = DG.markerClusterGroup();  
            this.markerLayer = DG.markerClusterGroup({
                zoomToBoundsOnClick: false
            }).addTo(this.map);
            this.map.addLayer(this.markerLayer);

            this.markerLayer.on('clusterclick', (e) => {
                const markers = e.layer.getAllChildMarkers();
                const bounds = DG.featureGroup(markers).getBounds();
                this.map.fitBounds(bounds);
            });
        },
        async loadMarkers() {
            try {
                const response = await ajaxRequest('/map/get-markers');
                this.markers = response?.payload || [];
                this.renderMarkers();
            } catch (error) {
                console.error('Ошибка загрузки маркеров:', error);
            }
        },
        exitFullscreen() {
            if (document.fullscreenElement) {
                document.exitFullscreen();
            } else if (document.webkitFullscreenElement) {
                document.webkitExitFullscreen();
            } else if (document.msFullscreenElement) {
                document.msExitFullscreen();
            }

            const topLayer = document.getElementById("top-layer");
            if (topLayer) {
                topLayer.style.display = "none";
            }
        },
        renderMarkers() {
            this.markerLayer.clearLayers();

            const filteredMarkers = this.markers.filter(marker => {
                const matchRegion = this.selectedRegion === 'all' || marker.region_id === this.selectedRegion;

                const matchParams = this.selectedFilterParams.length === 0
                    || this.selectedFilterParams.every(
                        param => marker.filter_params_ids?.includes(String(param))
                    );

                return matchRegion && matchParams;
            });

            filteredMarkers.forEach(markerData => {
                let marker = DG.marker(markerData.coords, { 
                    icon: DG.icon({
                        iconUrl: '/images/map_marker.svg',
                        iconSize: [30, 36],
                        iconAnchor: [15, 25],
                    }),
                    // draggable: true,
                });
                // .addTo(this.markerLayer);

                this.markerLayer.addLayer(marker);

                marker.on('click', () => {
                    this.exitFullscreen();  // если fullscreen тогда закрывать его 
                    noScroll.hideScroll();
                    this.activeMarker = markerData;
                });                

                // marker.on('drag', function(e) {
                //     var lat = e.target._latlng.lat.toFixed(3),
                //         lng = e.target._latlng.lng.toFixed(3);

                //    console.log(lat + ', ' + lng);
                // });
            });

            if (this.markerLayer.getLayers().length > 0) {
                const bounds = this.markerLayer.getBounds();
                this.map.fitBounds(bounds, { padding: [20, 20] });
            }
        },
        openFilterPopup() {
            noScroll.hideScroll();
            this.tempSelectedRegion = this.selectedRegion;
            this.tempSelectedFilterParams = this.selectedFilterParams;
            this.showFilterPopup = true;
        },
        closeFilterPopup() {
            noScroll.showScroll();
            this.showFilterPopup = false;
        },
        closeMarkerPopup() {
            noScroll.showScroll();
            this.activeMarker = null;
        },
        resetFilters() {
            noScroll.showScroll();
            this.selectedRegion = 'all';
            this.tempSelectedRegion = 'all';
            this.tempSelectedFilterParams = [];
            this.selectedFilterParams = [];
            this.showFilterPopup = false;
            this.renderMarkers();
        },
        getRegionTitleById(id) {
            const region = this.regions.find(r => r.id == parseInt(id, 10));
            return region ? region.title : '';
        },
        clearRegionFilter() {
            this.selectedRegion = 'all';
        },
        getParamTitleById(id) {
            const param = this.filterParams.find(p => p.id == parseInt(id, 10));
            return param ? param.title : '';
        },
        removeParamFilter(id) {
            this.selectedFilterParams = this.selectedFilterParams.filter(p => p !== id);
            this.renderMarkers();
        },
        applyFilters() {
            noScroll.showScroll();
            this.selectedRegion = this.tempSelectedRegion;
            this.selectedFilterParams = [...this.tempSelectedFilterParams];
            this.showFilterPopup = false;
            this.renderMarkers();
        },
    },
}
</script>